import { useState } from 'react';

import { Contact, ContactCategoryPath } from '@/api/address-book/types';
import { ContactView } from '@/api/address-book-bff/types';
import SettingsIcon from '@/assets/svg/ui-elements/settings-gear.svg';
import { Avatar, Modal, theme, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { CategoryBadge } from '@/pages/address-book/components/category-badge';
import { AssignCategoriesModal } from '@/pages/address-book/modules/assign-categories-modal';

import {
  BadgeRow,
  Body,
  CategoryGroup,
  ContactAvatarImg,
  ContactRow,
  Divider,
} from './address-book-categories-modal.styled';

const MODAL_MAX_WIDTH = 504;

export type CategoriesModalContact = (Contact | ContactView) & { iconUrl?: string | null };

export type AddressBookCategoriesModalProps = {
  contact: CategoriesModalContact;
};

export const AddressBookCategoriesModal = () => {
  const { open, handleClose, props } = useGlobalModal('ADDRESS_BOOK_CATEGORIES_MODAL');
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const contact = props?.contact;
  const categories = contact?.categories ?? [];
  const grouped = categories.reduce<Record<string, ContactCategoryPath[]>>((acc, category) => {
    if (!acc[category.rootName]) acc[category.rootName] = [];
    acc[category.rootName].push(category);
    return acc;
  }, {});
  const rootCount = Object.keys(grouped).length;

  const onEditCategories = () => {
    handleClose();
    setIsAssignOpen(true);
  };

  return (
    <>
      <Modal
        open={open}
        onOpenChange={handleClose}
        title="User Categories"
        maxWidth={MODAL_MAX_WIDTH}
        primaryAction={{
          label: 'Edit Categories',
          icon: <SettingsIcon />,
          color: 'tertiary',
          variant: 'outline',
          onClick: onEditCategories,
        }}
      >
        <Body>
          <ContactRow>
            {contact?.iconUrl ? (
              <ContactAvatarImg src={contact.iconUrl} alt={contact.name ?? ''} />
            ) : (
              <Avatar size="sm" color="blue" initials={contact?.name ?? ''} />
            )}
            <Typography variant="body1" fontWeight={500} noWrap>
              {contact?.name}
            </Typography>
          </ContactRow>

          <Divider />

          {Object.entries(grouped).map(([rootName, rootCategories]) => (
            <CategoryGroup key={rootName}>
              <Typography variant="body3" color={theme.colors.text.secondary}>
                {`${rootName}:`}
              </Typography>
              <BadgeRow>
                {rootCategories.map((category) => (
                  <CategoryBadge category={category} key={category.nodeId} />
                ))}
              </BadgeRow>
            </CategoryGroup>
          ))}

          <Typography variant="body3" color={theme.colors.text.secondary}>
            {`${categories.length} values assigned across ${rootCount} ${
              rootCount === 1 ? 'category' : 'categories'
            }`}
          </Typography>
        </Body>
      </Modal>

      {contact && (
        <AssignCategoriesModal
          open={isAssignOpen}
          onOpenChange={setIsAssignOpen}
          contact={contact}
        />
      )}
    </>
  );
};
